import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PaperAirplaneIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PhotoIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import authFetch from '../../api.js';

const DigitalForms = () => {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formResponses, setFormResponses] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError('No authentication token found. Please log in.');
        return;
      }

      const [formsResponse, responsesResponse, pendingResponse] = await Promise.all([
        authFetch('/employee/forms'),
        authFetch('/employee/forms/responses'),
        authFetch('/employee/forms/pending')
      ]);
      
      setForms(formsResponse || []);
      setFormResponses(responsesResponse || {});
      setPendingSubmissions(pendingResponse || []);
      
    } catch (error) {
      console.error('Error fetching forms:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } else {
        setError('Failed to load forms. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const openForm = (form) => {
    setSelectedForm(form);
    setShowFormModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = {
        formId: selectedForm._id,
        responses: formResponses[selectedForm._id] || {},
        submittedAt: new Date().toISOString(),
        location: await getCurrentLocation()
      };

      if (isOnline) {
        await authFetch('/employee/forms/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        // Store for later submission
        const pending = JSON.parse(localStorage.getItem('pendingFormSubmissions') || '[]');
        pending.push(formData);
        localStorage.setItem('pendingFormSubmissions', JSON.stringify(pending));
        setPendingSubmissions(prev => [...prev, formData]);
      }

      setShowFormModal(false);
      setSelectedForm(null);
      alert(isOnline ? 'Form submitted successfully!' : 'Form saved for submission when online.');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentLocation = async () => {
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
      } catch (error) {
        console.error('Error getting location:', error);
        return null;
      }
    }
    return null;
  };

  const handleResponseChange = (questionId, value) => {
    setFormResponses(prev => ({
      ...prev,
      [selectedForm._id]: {
        ...prev[selectedForm._id],
        [questionId]: value
      }
    }));
  };

  const addPhoto = async (questionId) => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          try {
            const reader = new FileReader();
            reader.onload = (e) => {
              handleResponseChange(questionId, {
                type: 'photo',
                data: e.target.result,
                filename: file.name
              });
            };
            reader.readAsDataURL(file);
          } catch (error) {
            console.error('Error reading file:', error);
            alert('Failed to add photo. Please try again.');
          }
        }
      };
    }
  };

  const submitPendingForms = async () => {
    if (!isOnline || pendingSubmissions.length === 0) return;

    try {
      for (const submission of pendingSubmissions) {
        await authFetch('/employee/forms/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submission)
        });
      }

      localStorage.removeItem('pendingFormSubmissions');
      setPendingSubmissions([]);
      alert('All pending forms submitted successfully!');
    } catch (error) {
      console.error('Error submitting pending forms:', error);
      alert('Failed to submit some pending forms. Please try again.');
    }
  };

  const getFormTypeIcon = (type) => {
    switch (type) {
      case 'safety': return '🛡️';
      case 'quality': return '🔍';
      case 'maintenance': return '🔧';
      case 'inspection': return '📋';
      default: return '📄';
    }
  };

  const getFormTypeColor = (type) => {
    switch (type) {
      case 'safety': return 'bg-red-100 text-red-800';
      case 'quality': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-green-100 text-green-800';
      case 'inspection': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#173861] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading digital forms...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/employee/dashboard')}
              className="bg-[#173861] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employee/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Digital Forms</h1>
                <p className="text-sm text-gray-500">Complete safety, quality, and maintenance forms</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {!isOnline && (
                <div className="flex items-center space-x-2 text-orange-600">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span className="text-sm">Offline Mode</span>
                </div>
              )}
              {pendingSubmissions.length > 0 && isOnline && (
                <button
                  onClick={submitPendingForms}
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors duration-200 flex items-center"
                >
                  <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                  Submit Pending ({pendingSubmissions.length})
                </button>
              )}
              <button
                onClick={handleLogout}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Forms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form) => (
            <div key={form._id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{getFormTypeIcon(form.type)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{form.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getFormTypeColor(form.type)}`}>
                      {form.type}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">{form.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span>Estimated time: {form.estimatedTime || '5-10 min'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span>{form.questions?.length || 0} questions</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => openForm(form)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Fill Form
                </button>
                
                <button
                  onClick={() => {
                    setSelectedForm(form);
                    setShowPreview(true);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {forms.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No forms available</p>
            <p className="text-gray-400">Forms will appear here once they're assigned to you.</p>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showFormModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getFormTypeIcon(selectedForm.type)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedForm.title}</h3>
                  <p className="text-sm text-gray-500">{selectedForm.description}</p>
                </div>
              </div>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="p-6">
              <div className="space-y-6">
                {selectedForm.questions?.map((question, index) => (
                  <div key={question._id} className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          {question.text}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        
                        {question.type === 'text' && (
                          <input
                            type="text"
                            value={formResponses[selectedForm._id]?.[question._id] || ''}
                            onChange={(e) => handleResponseChange(question._id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required={question.required}
                          />
                        )}
                        
                        {question.type === 'textarea' && (
                          <textarea
                            value={formResponses[selectedForm._id]?.[question._id] || ''}
                            onChange={(e) => handleResponseChange(question._id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows="3"
                            required={question.required}
                          />
                        )}
                        
                        {question.type === 'select' && (
                          <select
                            value={formResponses[selectedForm._id]?.[question._id] || ''}
                            onChange={(e) => handleResponseChange(question._id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required={question.required}
                          >
                            <option value="">Select an option</option>
                            {question.options?.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                        
                        {question.type === 'radio' && (
                          <div className="space-y-2">
                            {question.options?.map((option) => (
                              <label key={option} className="flex items-center space-x-2">
                                <input
                                  type="radio"
                                  name={question._id}
                                  value={option}
                                  checked={formResponses[selectedForm._id]?.[question._id] === option}
                                  onChange={(e) => handleResponseChange(question._id, e.target.value)}
                                  className="text-blue-500 focus:ring-blue-500"
                                  required={question.required}
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'checkbox' && (
                          <div className="space-y-2">
                            {question.options?.map((option) => (
                              <label key={option} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  value={option}
                                  checked={formResponses[selectedForm._id]?.[question._id]?.includes(option) || false}
                                  onChange={(e) => {
                                    const current = formResponses[selectedForm._id]?.[question._id] || [];
                                    const newValue = e.target.checked
                                      ? [...current, option]
                                      : current.filter(item => item !== option);
                                    handleResponseChange(question._id, newValue);
                                  }}
                                  className="text-blue-500 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {question.type === 'photo' && (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => addPhoto(question._id)}
                              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                              <PhotoIcon className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-700">Add Photo</span>
                            </button>
                            {formResponses[selectedForm._id]?.[question._id]?.data && (
                              <div className="mt-2">
                                <img
                                  src={formResponses[selectedForm._id][question._id].data}
                                  alt="Form photo"
                                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                      Submit Form
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Form Preview Modal */}
      {showPreview && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getFormTypeIcon(selectedForm.type)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Form Preview</h3>
                  <p className="text-sm text-gray-500">{selectedForm.title}</p>
                </div>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {selectedForm.questions?.map((question, index) => (
                  <div key={question._id} className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          {question.text}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Type: {question.type}
                          {question.options && ` (${question.options.length} options)`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default DigitalForms; 